/**
 * How long a cold launch may wait on Auth0 before the app is shown anyway.
 *
 * WHY THIS EXISTS. Every hard load asks Auth0 for an access token before the
 * first screen renders — once in the router's token-priming guard, then again in
 * main.js, which mounts the app only when that call settles. When the stored
 * access token has expired the ask is a refresh-token grant over the network,
 * and `@auth0/auth0-spa-js` gives that 10 s and three attempts: a stalled
 * connection to Auth0's edge holds the call for ~30 s, and the two sequential
 * waits held the WebView blank for up to a minute. Members force-closed long
 * before that, which restarted the wait, until one launch happened to reach
 * Auth0 quickly — "white screen until I force close enough times" (2026-09-25).
 * RUM shows the stalls on every Singapore ISP, web and native alike, so it is
 * the network path to Auth0, not one carrier and not our code; what IS ours is
 * letting it gate the first paint.
 *
 * The budget is measured from when this module loads — when the JS bundle
 * starts, so WebView boot time is not counted — and shared by the two
 * launch-time waits that gate the first paint (the token-priming guard and the
 * mount in main.js), so they cannot stack.
 *
 * NOT bounded: the route-level `beforeEnter` guards that prime a token themselves
 * (`requireFeatureFlag`, `coursesGuard`, the Courses redeem and unlock routes).
 * A cold launch straight onto one of those routes while Auth0 is stalled still
 * waits for the refresh. Bounding the token wait alone would not fix that: the
 * Courses guard then calls the API (/v2/profile, /courses/access), and those
 * calls wait on the same stalled refresh through the 401 interceptor. Once
 * it is spent the waits stop blocking; the Auth0 call itself carries on and
 * commits its token when it lands, and the 401 interceptor covers any request
 * that goes out before then.
 *
 * 5 s sits above the p99 of a healthy `/oauth/token` from the app (~3.2 s in
 * RUM, 2026-09) so a slow-but-working refresh still completes before the first
 * paint, and far below the ~30 s a stalled one takes.
 */
export const LAUNCH_AUTH_BUDGET_MS = 5000;

/** What `race` resolves with when the budget ran out before the promise did. */
export const LAUNCH_BUDGET_SPENT = Symbol('launch-budget-spent');

/**
 * @param {Object} [deps]
 * @param {number} [deps.budgetMs]
 * @param {() => number} [deps.now]
 * @returns {{ race: (promise: Promise<unknown>) => Promise<unknown> }}
 */
export function createLaunchBudget({
  budgetMs = LAUNCH_AUTH_BUDGET_MS,
  now = () => Date.now(),
} = {}) {
  const startedAt = now();
  const remaining = () => Math.max(0, budgetMs - (now() - startedAt));

  /**
   * Resolve with the promise's value, or with LAUNCH_BUDGET_SPENT once the
   * remaining budget runs out — whichever comes first. Rejections pass through.
   */
  function race(promise) {
    let timer;
    const expiry = new Promise(resolve => {
      timer = setTimeout(() => resolve(LAUNCH_BUDGET_SPENT), remaining());
    });
    return Promise.race([promise, expiry]).finally(() => clearTimeout(timer));
  }

  return { race };
}

/** The one budget a launch shares. */
export const launchBudget = createLaunchBudget();

const AUTH0_CACHE_PREFIX = '@@auth0spajs@@::';
const AUTH0_USER_SUFFIX = '::@@user@@';

/**
 * Whether this device holds an Auth0 session from an earlier launch — a token
 * entry `@auth0/auth0-spa-js` wrote to localStorage (`cacheLocation:
 * 'localstorage'`). It says nothing about whether that session is still valid;
 * it only separates "a member who was signed in and whose refresh is slow" from
 * "nobody has signed in on this device", which is the one distinction a launch
 * that stopped waiting needs.
 *
 * @param {Storage} [storage]
 * @param {string} [clientId]
 * @returns {boolean}
 */
export function hasStoredAuth0Session(
  storage = globalThis.localStorage,
  clientId = process.env.VUE_APP_AUTH0_CLIENT_ID,
) {
  if (!storage || !clientId) return false;
  const prefix = `${AUTH0_CACHE_PREFIX}${clientId}::`;
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix) && !key.endsWith(AUTH0_USER_SUFFIX)) return true;
    }
  } catch {
    // Storage unavailable: behave as if nothing is stored.
  }
  return false;
}

/**
 * Should the first navigation of a launch be let through while Auth0 is still
 * checking the session? Only when all three hold: it IS the first navigation,
 * Auth0 has not finished (`isLoading`), and the device holds a session to check.
 * Anyone else gets the normal signed-in check — a device with no stored session
 * never waits on the network, so it has no reason to be admitted early.
 *
 * @param {{ isInitialNavigation: boolean, isLoading: boolean, hasStoredSession: boolean }} state
 * @returns {boolean}
 */
export function shouldAdmitWhileAuthLoads({ isInitialNavigation, isLoading, hasStoredSession }) {
  return isInitialNavigation === true && isLoading === true && hasStoredSession === true;
}
